import { ChevronDownIcon } from './Icons'

interface JumpToLiveButtonProps {
  /** Whether the panel is currently scrolled away from its live edge. */
  show: boolean
  onClick: () => void
  /** Accessible name (screen reader + hover tooltip); the button itself is icon-only. */
  label: string
  testId?: string
}

/**
 * Floating "jump to the live edge" affordance scoped to a single scrollable
 * panel. Rendered as a compact down-arrow rather than a text pill so it stays
 * unobtrusive across narrow columns (Key Points, Actions, transcript panes);
 * `label` still supplies the accessible name via `aria-label`/`title`. Dimmed
 * by default (`opacity-60`) so it doesn't obscure the content scrolling
 * underneath it, going fully opaque on hover to signal it's interactive.
 */
export default function JumpToLiveButton({ show, onClick, label, testId }: JumpToLiveButtonProps) {
  return (
    <div
      className={[
        'pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center transition-all duration-200 ease-out',
        show ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
      ].join(' ')}
      aria-hidden={!show}
      data-testid={testId}
    >
      <button
        type="button"
        tabIndex={show ? 0 : -1}
        onClick={onClick}
        aria-label={label}
        title={label}
        className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white opacity-60 shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5 hover:opacity-100"
      >
        <ChevronDownIcon className="h-4 w-4" />
      </button>
    </div>
  )
}
